import { useState, useEffect, useRef } from "react";
import Navbar from "@/components/layout/Navbar";
import GuestOverlay from "@/components/auth/GuestOverlay";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getKieApiKey, getKieApiEndpoint } from "@/lib/kie-service";
import {
    Eraser,
    Loader2,
    Download,
    Coins,
    CheckCircle2,
    AlertTriangle,
    RefreshCw,
    Clock,
    History,
    Upload,
    FolderOpen,
    Trash2,
    Image as ImageIcon,
    Check,
    X
} from "lucide-react";

const HISTORY_KEY = "removebg_history";
const EXPIRY_DAYS = 3;
const CREDITS_REQUIRED = 2;

interface JobResult {
    status: "pending" | "processing" | "completed" | "error";
    taskId?: string;
    resultUrl?: string;
    message?: string;
}

interface HistoryItem {
    taskId: string;
    sourceUrl: string;
    resultUrl: string;
    createdAt: number;
    expiresAt: number;
}

interface GalleryImage {
    name: string;
    url: string;
    created_at: string;
}

const RemoveBackground = () => {
    const { t } = useLanguage();
    const { user, profile, loading: authLoading } = useAuth();
    const { toast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [imageUrl, setImageUrl] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [jobResult, setJobResult] = useState<JobResult | null>(null);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
    const [userCredits, setUserCredits] = useState(0);

    // Gallery modal state
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
    const [loadingGallery, setLoadingGallery] = useState(false);

    // Load history and user credits on mount
    useEffect(() => {
        loadHistory();
        if (user) {
            fetchUserCredits();
        }
    }, [user]);

    const fetchUserCredits = async () => {
        if (!user) return;
        try {
            const { data } = await supabase
                .from("profiles")
                .select("credits")
                .eq("user_id", user.id)
                .single();
            if (data) setUserCredits(data.credits || 0);
        } catch (e) {
            console.error("Error fetching credits:", e);
        }
    };

    const loadHistory = () => {
        try {
            const stored = localStorage.getItem(HISTORY_KEY);
            if (stored) {
                const items: HistoryItem[] = JSON.parse(stored);
                const now = Date.now();
                const validItems = items.filter(item => item.expiresAt > now);
                setHistoryItems(validItems);
                if (validItems.length !== items.length) {
                    localStorage.setItem(HISTORY_KEY, JSON.stringify(validItems));
                }
            }
        } catch (e) {
            console.error("Error loading history:", e);
        }
    };

    const saveToHistory = (item: HistoryItem) => {
        const updated = [item, ...historyItems].slice(0, 20);
        setHistoryItems(updated);
        localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
    };

    const formatTimeRemaining = (expiresAt: number): string => {
        const now = Date.now();
        const remaining = expiresAt - now;
        if (remaining <= 0) return t("หมดอายุแล้ว", "Expired");

        const hours = Math.floor(remaining / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);
        const remainingHours = hours % 24;

        if (days > 0) {
            return `${days} ${t("วัน", "days")} ${remainingHours} ${t("ชม.", "hrs")}`;
        }
        return `${hours} ${t("ชั่วโมง", "hours")}`;
    };

    // Image upload handler
    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast({
                title: t("ข้อผิดพลาด", "Error"),
                description: t("กรุณาเลือกไฟล์รูปภาพเท่านั้น", "Please select image files only"),
                variant: "destructive",
            });
            return;
        }

        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast({
                title: t("ข้อผิดพลาด", "Error"),
                description: t("ไฟล์ต้องมีขนาดไม่เกิน 5MB", "File size must be under 5MB"),
                variant: "destructive",
            });
            return;
        }

        setIsUploading(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `removebg-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
            const filePath = `temp/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('generation-inputs')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('generation-inputs')
                .getPublicUrl(filePath);

            setImageUrl(publicUrl);
            setPreviewUrl(publicUrl);
            toast({ title: t("อัพโหลดสำเร็จ", "Upload successful"), description: t("อัพโหลดรูปภาพเรียบร้อยแล้ว", "Image uploaded successfully") });
        } catch (err: any) {
            console.error("Upload error:", err);
            toast({
                title: t("ข้อผิดพลาด", "Error"),
                description: err.message,
                variant: "destructive",
            });
        } finally {
            setIsUploading(false);
        }
    };

    // Gallery functions
    const fetchGalleryImages = async () => {
        setLoadingGallery(true);
        try {
            // Fetch from multiple folders
            const folders = ['temp', 'articles', 'generation-outputs'];
            let allImages: GalleryImage[] = [];

            for (const folder of folders) {
                const { data, error } = await supabase.storage
                    .from('generation-inputs')
                    .list(folder, {
                        limit: 50,
                        sortBy: { column: 'created_at', order: 'desc' }
                    });

                if (!error && data) {
                    const images = data
                        .filter(file => file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i))
                        .map(file => {
                            const { data: { publicUrl } } = supabase.storage
                                .from('generation-inputs')
                                .getPublicUrl(`${folder}/${file.name}`);
                            return {
                                name: file.name,
                                url: publicUrl,
                                created_at: file.created_at || ''
                            };
                        });
                    allImages = [...allImages, ...images];
                }
            }

            // Sort by created_at descending
            allImages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            setGalleryImages(allImages.slice(0, 100));
        } catch (err: any) {
            console.error("Error fetching gallery:", err);
            toast({
                title: t("ข้อผิดพลาด", "Error"),
                description: t("ไม่สามารถโหลดรูปภาพจาก Gallery ได้", "Failed to load images from Gallery"),
                variant: "destructive",
            });
        } finally {
            setLoadingGallery(false);
        }
    };

    const openGallery = () => {
        setShowGalleryModal(true);
        fetchGalleryImages();
    };

    const selectGalleryImage = (url: string) => {
        setImageUrl(url);
        setPreviewUrl(url);
        setShowGalleryModal(false);
        toast({ title: t("เลือกรูปสำเร็จ", "Image selected"), description: t("เลือกรูปภาพจาก Gallery เรียบร้อยแล้ว", "Image selected from Gallery") });
    };

    const clearImage = () => {
        setImageUrl("");
        setPreviewUrl("");
        setJobResult(null);
    };

    const deductCredits = async (): Promise<boolean> => {
        if (!user) return false;

        try {
            const { error } = await supabase.rpc("deduct_credits" as any, {
                user_id: user.id,
                amount: CREDITS_REQUIRED
            });

            if (error) {
                console.error("Error deducting credits:", error);
                return false;
            }

            setUserCredits(prev => prev - CREDITS_REQUIRED);
            return true;
        } catch (e) {
            console.error("Error deducting credits:", e);
            return false;
        }
    };

    const refundCredits = async () => {
        if (!user) return;

        try {
            await supabase.rpc("add_credits" as any, {
                user_id: user.id,
                amount: CREDITS_REQUIRED
            });
            setUserCredits(prev => prev + CREDITS_REQUIRED);
        } catch (e) {
            console.error("Error refunding credits:", e);
        }
    };

    const handleRemoveBackground = async () => {
        if (!imageUrl.trim()) {
            toast({
                title: t("กรุณาเลือกรูปภาพ", "Please select an image"),
                description: t("อัพโหลดหรือเลือกรูปภาพจาก Gallery", "Upload or select image from Gallery"),
                variant: "destructive"
            });
            return;
        }

        if (!user) {
            toast({
                title: t("กรุณาเข้าสู่ระบบ", "Please login"),
                description: t("ต้องเข้าสู่ระบบเพื่อใช้บริการนี้", "Login required for this service"),
                variant: "destructive"
            });
            return;
        }

        if (userCredits < CREDITS_REQUIRED) {
            toast({
                title: t("เครดิตไม่เพียงพอ", "Insufficient credits"),
                description: t(`ต้องใช้ ${CREDITS_REQUIRED} เครดิต`, `Requires ${CREDITS_REQUIRED} credits`),
                variant: "destructive"
            });
            return;
        }

        setIsProcessing(true);
        setJobResult({ status: "pending" });

        // Deduct credits first
        const deducted = await deductCredits();
        if (!deducted) {
            setIsProcessing(false);
            setJobResult(null);
            toast({
                title: t("ข้อผิดพลาด", "Error"),
                description: t("ไม่สามารถหักเครดิตได้", "Failed to deduct credits"),
                variant: "destructive"
            });
            return;
        }

        try {
            const apiKey = await getKieApiKey();
            const endpoint = await getKieApiEndpoint();

            // Create task
            const createResponse = await fetch(`${endpoint}/api/v1/jobs/createTask`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiKey}`
                },
                body: JSON.stringify({
                    model: "recraft/remove-background",
                    input: {
                        image: imageUrl
                    }
                })
            });

            const createData = await createResponse.json();

            if (createData.code !== 200 || !createData.data?.taskId) {
                throw new Error(createData.msg || "Failed to create task");
            }

            const taskId = createData.data.taskId;
            setJobResult({ status: "processing", taskId });

            // Poll for result
            await pollTaskStatus(taskId);

        } catch (error: any) {
            console.error("Error:", error);
            await refundCredits();
            setJobResult({
                status: "error",
                message: error.message || t("เกิดข้อผิดพลาด", "An error occurred")
            });
            toast({
                title: t("ข้อผิดพลาด", "Error"),
                description: error.message,
                variant: "destructive"
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const pollTaskStatus = async (taskId: string) => {
        const apiKey = await getKieApiKey();
        const endpoint = await getKieApiEndpoint();
        const maxAttempts = 60;
        let attempts = 0;

        const poll = async () => {
            attempts++;
            setCheckingStatus(true);

            try {
                const response = await fetch(
                    `${endpoint}/api/v1/jobs/recordInfo?taskId=${taskId}`,
                    {
                        headers: {
                            "Authorization": `Bearer ${apiKey}`
                        }
                    }
                );

                const data = await response.json();

                if (data.code !== 200) {
                    throw new Error(data.msg || "Failed to check status");
                }

                const { state, resultJson, failMsg } = data.data;

                if (state === "success") {
                    const result = JSON.parse(resultJson);
                    const resultUrl = result.resultUrls?.[0];

                    if (resultUrl) {
                        setJobResult({
                            status: "completed",
                            taskId,
                            resultUrl
                        });

                        // Save to history
                        saveToHistory({
                            taskId,
                            sourceUrl: imageUrl,
                            resultUrl,
                            createdAt: Date.now(),
                            expiresAt: Date.now() + (EXPIRY_DAYS * 24 * 60 * 60 * 1000)
                        });

                        toast({
                            title: t("สำเร็จ!", "Success!"),
                            description: t("ลบพื้นหลังเรียบร้อยแล้ว", "Background removed successfully")
                        });

                        // Log generation
                        if (user) {
                            await supabase.from("generation_logs").insert({
                                user_id: user.id,
                                service_type: "remove-bg",
                                credits_used: CREDITS_REQUIRED,
                                prompt: imageUrl,
                                result_url: resultUrl,
                                status: "completed"
                            });
                        }
                    }
                    setCheckingStatus(false);
                    return;
                }

                if (state === "fail") {
                    await refundCredits();
                    setJobResult({
                        status: "error",
                        taskId,
                        message: failMsg || t("การประมวลผลล้มเหลว", "Processing failed")
                    });
                    setCheckingStatus(false);
                    return;
                }

                // Still processing
                if (attempts < maxAttempts) {
                    setTimeout(poll, 3000);
                } else {
                    await refundCredits();
                    setJobResult({
                        status: "error",
                        taskId,
                        message: t("หมดเวลารอผลลัพธ์", "Timeout waiting for result")
                    });
                    setCheckingStatus(false);
                }
            } catch (error: any) {
                console.error("Poll error:", error);
                if (attempts < maxAttempts) {
                    setTimeout(poll, 5000);
                } else {
                    await refundCredits();
                    setJobResult({
                        status: "error",
                        message: error.message
                    });
                    setCheckingStatus(false);
                }
            }
        };

        poll();
    };

    const downloadResult = async (url: string, filename: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (e) {
            window.open(url, "_blank");
        }
    };

    if (authLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Navbar />
            {!user && <GuestOverlay />}

            <main className="pt-24 pb-16">
                <div className="container mx-auto px-4 max-w-4xl">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <Badge variant="secondary" className="mb-4">
                            <Eraser className="h-3 w-3 mr-1" />
                            {t("ลบพื้นหลัง", "Remove Background")}
                        </Badge>
                        <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
                            {t("ลบพื้นหลังด้วย AI", "AI Background Removal")}
                        </h1>
                        <p className="text-muted-foreground">
                            {t(
                                "อัพโหลดรูปภาพหรือเลือกจาก Gallery เพื่อลบพื้นหลังอัตโนมัติ",
                                "Upload image or select from Gallery to automatically remove background"
                            )}
                        </p>
                    </div>

                    {/* Credits Info */}
                    {user && (
                        <Card variant="glass" className="mb-6">
                            <CardContent className="py-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Coins className="h-5 w-5 text-primary" />
                                        <span className="font-medium">{t("เครดิตของคุณ", "Your credits")}</span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-2xl font-bold text-primary">{userCredits}</span>
                                        <Badge variant="outline">
                                            {t(`ใช้ ${CREDITS_REQUIRED} เครดิต/ครั้ง`, `${CREDITS_REQUIRED} credits/use`)}
                                        </Badge>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Main Card */}
                    <Card variant="glass" className="mb-8">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Eraser className="h-5 w-5" />
                                {t("เลือกรูปภาพ", "Select Image")}
                            </CardTitle>
                            <CardDescription>
                                {t(
                                    "รองรับ PNG, JPG, WEBP ขนาดไม่เกิน 5MB ความละเอียดสูงสุด 4096px",
                                    "Supports PNG, JPG, WEBP up to 5MB, max resolution 4096px"
                                )}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Upload/Gallery buttons */}
                            <div className="flex flex-wrap gap-3">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleImageUpload}
                                />
                                <Button
                                    variant="outline"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isUploading || isProcessing}
                                    className="gap-2"
                                >
                                    {isUploading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Upload className="h-4 w-4" />
                                    )}
                                    {t("อัพโหลดรูปภาพ", "Upload Image")}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={openGallery}
                                    disabled={isProcessing}
                                    className="gap-2"
                                >
                                    <FolderOpen className="h-4 w-4" />
                                    {t("เลือกจาก Gallery", "Select from Gallery")}
                                </Button>
                            </div>

                            {/* Preview */}
                            {previewUrl && (
                                <div className="relative">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Original */}
                                        <div className="space-y-2">
                                            <span className="text-sm font-medium text-muted-foreground">
                                                {t("รูปต้นฉบับ", "Original")}
                                            </span>
                                            <div className="relative rounded-lg overflow-hidden border border-border bg-muted/30">
                                                <img
                                                    src={previewUrl}
                                                    alt="Original"
                                                    className="w-full h-auto max-h-80 object-contain"
                                                />
                                                <Button
                                                    variant="destructive"
                                                    size="icon"
                                                    className="absolute top-2 right-2 h-8 w-8"
                                                    onClick={clearImage}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Result */}
                                        <div className="space-y-2">
                                            <span className="text-sm font-medium text-muted-foreground">
                                                {t("ผลลัพธ์", "Result")}
                                            </span>
                                            <div className="relative rounded-lg overflow-hidden border border-border bg-[url('/checkerboard.svg')] bg-repeat min-h-[200px] flex items-center justify-center">
                                                {jobResult?.status === "completed" && jobResult.resultUrl ? (
                                                    <img
                                                        src={jobResult.resultUrl}
                                                        alt="Result"
                                                        className="w-full h-auto max-h-80 object-contain"
                                                    />
                                                ) : jobResult?.status === "processing" ? (
                                                    <div className="flex flex-col items-center gap-2 p-8">
                                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                                        <span className="text-sm text-muted-foreground">
                                                            {t("กำลังประมวลผล...", "Processing...")}
                                                        </span>
                                                    </div>
                                                ) : jobResult?.status === "error" ? (
                                                    <div className="flex flex-col items-center gap-2 p-8 text-destructive">
                                                        <AlertTriangle className="h-8 w-8" />
                                                        <span className="text-sm text-center">
                                                            {jobResult.message}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center gap-2 p-8 text-muted-foreground">
                                                        <ImageIcon className="h-8 w-8 opacity-50" />
                                                        <span className="text-sm">
                                                            {t("รอผลลัพธ์", "Waiting for result")}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action buttons */}
                            <div className="flex flex-wrap gap-3">
                                <Button
                                    onClick={handleRemoveBackground}
                                    disabled={!imageUrl || isProcessing || !user}
                                    className="gap-2"
                                    variant="hero"
                                >
                                    {isProcessing ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Eraser className="h-4 w-4" />
                                    )}
                                    {t("ลบพื้นหลัง", "Remove Background")}
                                </Button>

                                {jobResult?.status === "completed" && jobResult.resultUrl && (
                                    <Button
                                        variant="outline"
                                        onClick={() => downloadResult(jobResult.resultUrl!, `removebg-${Date.now()}.png`)}
                                        className="gap-2"
                                    >
                                        <Download className="h-4 w-4" />
                                        {t("ดาวน์โหลด", "Download")}
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* History */}
                    {historyItems.length > 0 && (
                        <Card variant="glass">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <History className="h-5 w-5" />
                                    {t("ประวัติการใช้งาน", "History")}
                                </CardTitle>
                                <CardDescription>
                                    {t(
                                        `ผลลัพธ์จะหมดอายุใน ${EXPIRY_DAYS} วัน`,
                                        `Results expire in ${EXPIRY_DAYS} days`
                                    )}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {historyItems.map((item) => (
                                        <div
                                            key={item.taskId}
                                            className="relative group rounded-lg overflow-hidden border border-border bg-muted/30"
                                        >
                                            <img
                                                src={item.resultUrl}
                                                alt="Result"
                                                className="w-full h-24 object-cover"
                                            />
                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="h-8 w-8 text-white hover:text-white hover:bg-white/20"
                                                    onClick={() => downloadResult(item.resultUrl, `removebg-${item.taskId}.png`)}
                                                >
                                                    <Download className="h-4 w-4" />
                                                </Button>
                                            </div>
                                            <div className="absolute bottom-1 right-1">
                                                <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                                                    <Clock className="h-3 w-3 mr-1" />
                                                    {formatTimeRemaining(item.expiresAt)}
                                                </Badge>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </main>

            {/* Gallery Modal */}
            <Dialog open={showGalleryModal} onOpenChange={setShowGalleryModal}>
                <DialogContent className="max-w-4xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FolderOpen className="h-5 w-5" />
                            {t("เลือกรูปภาพจาก Gallery", "Select from Gallery")}
                        </DialogTitle>
                        <DialogDescription>
                            {t("เลือกรูปภาพที่อัพโหลดไว้แล้ว", "Select a previously uploaded image")}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end mb-4">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchGalleryImages}
                            disabled={loadingGallery}
                            className="gap-2"
                        >
                            <RefreshCw className={`h-4 w-4 ${loadingGallery ? 'animate-spin' : ''}`} />
                            {t("รีเฟรช", "Refresh")}
                        </Button>
                    </div>
                    {loadingGallery ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : galleryImages.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <ImageIcon className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>{t("ยังไม่มีรูปภาพใน Gallery", "No images in Gallery")}</p>
                            <p className="text-sm mt-2">{t("อัพโหลดรูปภาพใหม่เพื่อใช้งาน", "Upload new images to use")}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-[50vh] overflow-y-auto p-1">
                            {galleryImages.map((image) => (
                                <div
                                    key={image.name}
                                    className="relative group cursor-pointer rounded-lg overflow-hidden border border-border hover:border-primary transition-colors"
                                    onClick={() => selectGalleryImage(image.url)}
                                >
                                    <img
                                        src={image.url}
                                        alt={image.name}
                                        className="w-full h-24 object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <Check className="h-8 w-8 text-white" />
                                    </div>
                                    {imageUrl === image.url && (
                                        <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-1">
                                            <Check className="h-3 w-3" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RemoveBackground;
